/**
 * Card genérico para documentos de banco de dados (veículos, produtos, serviços, documentos)
 * Usado como fallback quando o tipo não é 'properties'
 */

import React from 'react';
import type { DatabaseDocumentResponse } from '@troia-v3/types';
import { formatPrice } from './PropertyCard';
import { Icon } from '@/shared/icons'
import type { IconName } from '@/shared/icons'
export interface GenericDocumentCardProps {
  document: DatabaseDocumentResponse<any>;
  onClick?: () => void;
  alwaysLight?: boolean;
}

const TYPE_CONFIG: Record<string, { icon: IconName; label: string; color: string }> = {
  vehicles: { icon: 'truck', label: 'Veículo', color: '#3B82F6' },
  products: { icon: 'package', label: 'Produto', color: '#10B981' },
  services: { icon: 'tool', label: 'Serviço', color: '#F59E0B' },
  documents: { icon: 'file-text', label: 'Documento', color: '#6366F1' },
};

export const GenericDocumentCard: React.FC<GenericDocumentCardProps> = ({ document, onClick, alwaysLight }) => {
  const data = document.data;
  const config = TYPE_CONFIG[document.type] || TYPE_CONFIG.documents;

  const firstPhoto = data?.media?.photos?.[0];
  const title = data?.title || data?.name || (data?.brand && data?.model ? `${data.brand} ${data.model}` : 'Sem título');
  const price = data?.opportunities?.[0]?.pricing?.amount;

  const cardBg = alwaysLight ? '#ffffff' : 'var(--theme-card-background)';
  const cardBorder = alwaysLight ? '#e5e7eb' : 'var(--theme-card-border)';
  const cardText = alwaysLight ? '#1e293b' : 'var(--theme-card-text)';
  const mutedText = alwaysLight ? '#64748b' : 'var(--theme-muted-text)';
  const mutedBg = alwaysLight ? '#f1f5f9' : 'var(--theme-muted-background, #f1f5f9)';

  return (
    <div
      className="rounded-lg border overflow-hidden hover:shadow-lg transition-all duration-200 cursor-pointer group"
      style={{ backgroundColor: cardBg, borderColor: cardBorder }}
      onClick={onClick}
      data-testid={`generic-document-card-${document.id}`}
    >
      {/* Imagem ou Ícone */}
      <div className="relative aspect-[4/3] overflow-hidden">
        {firstPhoto ? (
          <img
            src={firstPhoto}
            alt={title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div
            className="w-full h-full flex items-center justify-center"
            style={{ backgroundColor: mutedBg }}
          >
            <Icon name={config.icon} className="w-10 h-10" style={{ color: mutedText }} />
          </div>
        )}

        {/* Badge de Preço */}
        {price && (
          <div
            className="absolute top-2 left-2 px-2 py-1 rounded text-xs font-semibold shadow-sm"
            style={{ backgroundColor: config.color, color: '#ffffff' }}
          >
            {formatPrice(price)}
          </div>
        )}

        {/* Badge de Tipo */}
        <div
          className="absolute top-2 right-2 px-2 py-1 rounded text-xs font-medium shadow-sm"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)', color: '#ffffff' }}
        >
          {config.label}
        </div>
      </div>

      {/* Content */}
      <div className="p-3">
        <h3
          className="font-semibold text-sm mb-1.5 line-clamp-2"
          style={{ color: cardText }}
        >
          {title}
        </h3>

        {data?.description && (
          <p className="text-xs line-clamp-2" style={{ color: mutedText }}>
            {data.description}
          </p>
        )}
      </div>
    </div>
  );
};
